<template>
    <div>
        <b-dropdown
            id="big-green-new-button"
            right
            size="lg"
            dropup
            no-caret
            variant="success"
            boundary="window"
            toggle-class="rounded-pill px-5"
            menu-class="font-size-100"
        >
            <template #button-content>
                <b-icon icon="plus-circle" class="mr-2"></b-icon>
                {{ $splTrans('New') }}...
            </template>
            <b-dropdown-item v-if="canAddVendor" @click="onResourceNewModal('Vendor')">
                <b-icon icon="building" variant="info" class="mr-2"></b-icon>
                New Vendor
            </b-dropdown-item>
            <b-dropdown-item
                v-if="onboardingEnabled"
                @click="showOnboardingStarter"
            >
                <b-icon
                    icon="box-arrow-in-right"
                    variant="primary"
                    class="mr-2"
                ></b-icon>
                New Vendor Onboarding
            </b-dropdown-item>
            
            <b-dropdown-item @click="addNewContact">
                <b-icon icon="person" variant="accent2" class="mr-2"></b-icon>
                New Contact
            </b-dropdown-item>
            <b-dropdown-item
                v-if="sourcingEnabled && $currentUserInfo.is_paid_seat"
                @click="addNewRfx"
            >
                <b-icon
                    icon="card-checklist"
                    variant="primary"
                    class="mr-2"
                ></b-icon>
                New Sourcing Event
            </b-dropdown-item>

            <!-- Sourcing Request Flow -->
            <b-dropdown-item v-if="sourcingEnabled && sourcingRequestEnabled" @click="addNewSourcingRequestFlow">
                <b-icon icon="box-arrow-in-right" variant="info" class="mr-2"></b-icon>
                New Sourcing Request    
            </b-dropdown-item>
            <b-dropdown-item
                v-if="$currentUserInfo.is_paid_seat"
                @click="addNewItem"
            >
                <b-icon icon="box" variant="accent" class="mr-2"></b-icon>
                New Item
            </b-dropdown-item>
            <b-dropdown-item
                v-if="qualityWorkflowRunEnabled"
                @click="addNewQualityIssue"
            >
                <b-icon
                    icon="patch-check"
                    variant="danger"
                    class="mr-2"
                ></b-icon>
                New Supplier Quality Issue
            </b-dropdown-item>
            <b-dropdown-item
                v-if="qualityIssueEnabled"
                @click="showQualityIssueStarter"
            >
                <b-icon
                    icon="patch-check"
                    variant="primary"
                    class="mr-2"
                ></b-icon>
                New Quality Issue
            </b-dropdown-item>
        </b-dropdown>

        <!-- modals here -->
        <rfx-create-modal ref="rfxCreateModal"></rfx-create-modal>

        <ResourceModal ref="resourceModal" />

        <!-- Keeping redirect here for creates... go to company page -->
        <contact-create-update-modal
            ref="contactModal"
            :hide-save-redirect="false"
        ></contact-create-update-modal>

        <item-create-update-modal ref="itemModal"></item-create-update-modal>

        <!-- WorkflowRun version -->
        <workflow-starter
            ref="qualityWorkflowStarter"
            spec-groups="_quality_module"
        ></workflow-starter>

        <!-- FlowObject / QualityIssue version - global component for now -->
        <FlowObjectStarter
            v-if="qualityIssueEnabled"
            ref="qualityStarter"
            flow-object-class="QualityIssue"
        ></FlowObjectStarter>

        <!-- Onboarding starter -->
        <FlowObjectStarter
            v-if="onboardingEnabled"
            ref="onboardingStarter"
            flow-object-class="Onboarding"
        ></FlowObjectStarter>

        <!-- Sourcing Request Flow Object -->
        <FlowObjectStarter
            v-if="sourcingRequestEnabled"
            ref="sourcingRequestFlowStarter"
            flow-object-class="SourcingRequest"
        ></FlowObjectStarter>

    </div>
</template>

<script>
// put other components in here... then put this button in the sidebar
import RfxCreateModal from "./RfxCreateModal.vue";
import ResourceModal from "@/components/resources/ResourceModal";
import ContactCreateUpdateModal from "./ContactCreateUpdateModal.vue";
import WorkflowStarter from "./demo-workflow/components/WorkflowStarter.vue";
import { currentUserInfoMixin, tenantInfoMixin } from "../js/custom-vue-mixins";

export default {
    name: "BigGreenNewButton",
    components: {
        RfxCreateModal,
        ResourceModal,
        ContactCreateUpdateModal,
        WorkflowStarter,
    },
    mixins: [
        currentUserInfoMixin,
        tenantInfoMixin, // use this.tenantInfo -- has { custom_flags, settings, tenant_name, tenant_id }
    ],
    props: {},
    data: function () {
        return {};
    },
    computed: {
        qualityWorkflowRunEnabled() {
            // tenant must have show_quality enabled
            const hideDwfQualityCreateButton = !!this.tenantInfo.custom_flags?.hide_dwf_quality_create_button || !!this.tenantInfo.custom_flags?.hide_dwfrun_quality;
            return (
                this.tenantInfo.custom_flags?.show_quality && !hideDwfQualityCreateButton
            );
        },
        qualityIssueEnabled() {
            // tenant must have show_new_quality enabled 
            return (
                this.tenantInfo.custom_flags?.show_new_quality // && this.$currentUserInfo.is_paid_seat  // is_paid_seat... just temporary until fully ready 
            );
        },
        sourcingEnabled() {
            return !this.tenantInfo.custom_flags?.hide_sourcing;  // NOT hide_sourcing
        },
        sourcingRequestEnabled() {
            // Check if sourcing request feature is enabled - and if TU can see it -- TODO change later
            return (
                this.tenantInfo.custom_flags?.show_sourcing_request
            )
        },
        onboardingEnabled() {
            return (
                this.tenantInfo.custom_flags?.show_onboarding && this.$currentUserInfo.is_paid_seat
            )
        },
        canAddVendor() {
            // TODO - base this on new backend permissions -- current front-end hiding is short-term
            return this.$currentUserInfo?.is_paid_seat || this.$tenantInfo.custom_flags?.all_internal_can_create_vendors || false;
        },
    },
    methods: {
        onResourceNewModal(resourceType) {
            this.$refs.resourceModal?.new({ type: resourceType });
        },
        addNewRfx: function () {
            this.$refs.rfxCreateModal?.showModal();
        },
        addNewContact: function () {
            this.$refs.contactModal?.startCreate();
        },
        addNewItem: function () {
            this.$refs.itemModal?.startCreate();
        },
        addNewQualityIssue() {
            // dashboard page calls this via $ref - don't change unless you change that 
            if (this.$refs.qualityWorkflowStarter) {
                this.$refs.qualityWorkflowStarter.show();
            }
        },
        showQualityIssueStarter() {
            const starter = this.$refs.qualityStarter;
            if (starter) {
                starter.show();
            }
        },
        showOnboardingStarter() {
            const starter = this.$refs.onboardingStarter;
            if (starter) {
                starter.show();
            }
        },
        addNewSourcingRequestFlow() {
            if(this.$refs.sourcingRequestFlowStarter) {
                this.$refs.sourcingRequestFlowStarter.show()
            }
        }
    },
};
</script>

<style>
#big-green-new-button .dropdown-menu li a {
    padding-left: 1rem;
    padding-right: 1rem;
}
</style>
